import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Panel, Empty } from "../components/ui";
import {
  todayParis,
  formatDate,
  monthGrid,
  shiftMonth,
} from "../utils/calendar.mjs";

const labels = {
  follow: "Relance",
  appointment: "RDV",
  engagement: "Engagement",
  email: "Email",
  quote: "Devis",
  contact: "Contact",
};
const plannedDate = (task) =>
  task.next_due || task.scheduled_date || task.due_date_original;
export default function Agenda({ state, tasks, onTask, onNewTask }) {
  const today = todayParis();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selected, setSelected] = useState(today);
  const entries = tasks.filter(
    (t) => !t.done && !["call", "manager"].includes(t.category),
  );
  const forDay = (date) =>
    entries
      .filter((t) => plannedDate(t) === date)
      .sort((a, b) => (a.time || "23:59").localeCompare(b.time || "23:59"));
  const clientName = (task) =>
    state.clients.find((c) => c.id === task.clientId)?.name || "Établissement";
  const monthLabel = new Date(`${month}-01T12:00:00Z`).toLocaleDateString(
    "fr-FR",
    { month: "long", year: "numeric", timeZone: "Europe/Paris" },
  );
  const selectedLabel = new Date(`${selected}T12:00:00Z`).toLocaleDateString(
    "fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" },
  );
  function changeMonth(value) {
    setMonth(value);
    setSelected(value === today.slice(0, 7) ? today : `${value}-01`);
  }
  return (
    <>
      <Panel
        title="Agenda"
        action={
          <button className="secondary" onClick={onNewTask}>
            <Plus /> Ajouter une tâche
          </button>
        }
      >
        <div className="monthToolbar">
          <h3>{monthLabel}</h3>
          <div className="dateControls">
            <button
              className="secondary"
              aria-label="Mois précédent"
              onClick={() => changeMonth(shiftMonth(month, -1))}
            >
              <ChevronLeft />
            </button>
            <label className="monthPicker">
              Mois
              <input
                type="month"
                aria-label="Mois de l’agenda"
                value={month}
                onChange={(e) => {
                  if (e.target.value) changeMonth(e.target.value);
                }}
              />
            </label>
            <button
              className="secondary"
              aria-label="Mois suivant"
              onClick={() => changeMonth(shiftMonth(month, 1))}
            >
              <ChevronRight />
            </button>
            <button
              className="secondary"
              onClick={() => {
                setMonth(today.slice(0, 7));
                setSelected(today);
              }}
            >
              Aujourd’hui
            </button>
          </div>
        </div>
        <p className="hint">
          Cliquez sur une action pour la traiter, ou sur un jour pour lire tous
          ses détails. Les suivis en retard sont affichés à leur date de report
          ; l’échéance initiale reste conservée.
        </p>
        <div className="monthWeekdays">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="monthGrid" aria-label={`Calendrier de ${monthLabel}`}>
          {monthGrid(month).map((date) => {
            const events = forDay(date),
              outside = !date.startsWith(month);
            return (
              <div
                key={date}
                className={`monthCell ${outside ? "outsideMonth" : ""} ${date === today ? "calendarToday" : ""} ${date === selected ? "calendarSelected" : ""}`}
              >
                <button
                  className="monthDay"
                  aria-label={`Voir le ${formatDate(date)}`}
                  aria-pressed={date === selected}
                  onClick={() => {
                    setSelected(date);
                    if (outside) setMonth(date.slice(0, 7));
                  }}
                >
                  <span>{Number(date.slice(-2))}</span>
                  {date === today && <small>Aujourd’hui</small>}
                </button>
                {events.slice(0, 2).map((task) => (
                  <button
                    key={task.id}
                    className={`calendarEvent event-${task.category}`}
                    onClick={() => onTask(task)}
                    title={`${task.time || "Sans horaire"} · ${clientName(task)} · ${task.title}`}
                  >
                    <b>
                      {task.time || labels[task.category] || "Suivi"}
                      {task.due_date_original < today && (
                        <span className="calendarLate" aria-label="En retard">
                          {" "}
                          ●
                        </span>
                      )}
                    </b>
                    <span>{clientName(task)}</span>
                    <small>{task.title}</small>
                  </button>
                ))}
                {events.length > 2 && (
                  <button
                    className="calendarMore"
                    onClick={() => setSelected(date)}
                  >
                    +{events.length - 2} autres
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="calendarLegend">
          <span>● RDV / relances</span>
          <span>● Engagements</span>
          <span>● Devis / contacts / emails</span>
          <span className="calendarLate">● En retard</span>
        </div>
      </Panel>
      <Panel title={selectedLabel}>
        <div className="calendarDayDetails">
          {forDay(selected).map((task) => (
            <button
              className="calendarDetail"
              key={task.id}
              onClick={() => onTask(task)}
            >
              <strong>{task.time || "Sans horaire"}</strong>
              <div>
                <b>
                  {clientName(task)} · {task.title}
                </b>
                <small>
                  {labels[task.category] || "Suivi"} · Échéance initiale :{" "}
                  {formatDate(task.due_date_original)}
                  {plannedDate(task) !== task.due_date_original
                    ? ` · Report au ${formatDate(plannedDate(task))}`
                    : ""}
                </small>
                {task.notes && <small>{task.notes}</small>}
              </div>
              {task.due_date_original < today && (
                <span className="calendarLate">En retard</span>
              )}
              <ChevronRight />
            </button>
          ))}
        </div>
        {!forDay(selected).length && (
          <Empty>Aucune action programmée pour cette journée.</Empty>
        )}
      </Panel>
    </>
  );
}
